import React, { useContext, useEffect, useRef, useState } from "react";

import { MEDIUM, SMALL, useDeviceInfo, useIntersectionObserver } from "@swa-ui/browser";
import { ChaseContext } from "@swa-ui/chase";
import { UrgencyTriggerBadge } from "@swa-ui/chase";
import { Badge, getLinearGradient, Icon, Image } from "@swa-ui/core";
import { isResponseOk, useFetch } from "@swa-ui/fetch";
import { usePersistedState } from "@swa-ui/persistence";
import { applyOverrides, CallToAction, PlacementsContext, TextContent } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { OFFERS_ALIGNMENT } from "../../defines";
import styles from "./OffersPlacement.module.scss";
import { offersPlacementPropType } from "./OffersPlacementPropTypes";
import { renderOfferDetails } from "./OfferUtils";
import { ShowTheMath } from "./ShowTheMath";

const FIRM_OFFER_CONFIRMATION_ENDPOINT = "/api/chase/v2/chase/firm-offer-confirmation";

export const OffersPlacement = (props) => {
  const { additionalProps, placement, placementData } = props;
  const {
    loading: { last: { context: { customerInfo, userLoggedIn } = {} } = {} },
  } = useContext(PlacementsContext);
  const { BOTTOM, LEFT, MIDDLE, RIGHT, TOP } = OFFERS_ALIGNMENT;
  const {
    background,
    callToAction,
    command,
    commandProps,
    disclaimer,
    eyebrow,
    graphic,
    offerDetails,
    position,
    showTheMath,
    text,
  } = placement ?? {};
  const { callout, primaryText, secondaryText } = text || {};
  const { isLargeOrXlarge, screenSize } = useDeviceInfo();
  const { align } = applyOverrides(graphic, screenSize);
  const { offset } = applyOverrides(position, screenSize);
  const { horizontal: horizontalAlignGraphic, vertical: verticalAlignGraphic } = align ?? {};
  // TODO: QUAMON-1217 Quasars tech debt to check if this re-run of applyOverrides is needed?
  const { align: ctaAlign } = applyOverrides(callToAction, screenSize);
  const { horizontal: horizontalAlignCTA, vertical: verticalAlignCTA } = ctaAlign ?? {};
  const { isChasePlacement = false, isChasePrequal = false, viewPort = 0 } = placementData || {};
  const { chaseDetails: { offerIdentifier } = {} } = useContext(ChaseContext);
  const { firstName, lastName, accountNumber, redeemablePoints } = customerInfo ?? {};
  const { put, response } = useFetch();
  const [viewedOffers, setViewedOffers] = usePersistedState({
    defaultValue: {},
    key: "viewed-chase-offer-ids",
  });
  const placementRef = useRef(null);
  const [firmOfferConfirmationCalled, setFirmOfferConfirmationCalled] = useState(false);
  const observerOptions = {
    freezeOnceVisible: isPlacementViewed(),
    threshold: parseFloat(viewPort) / 100,
  };

  useEffect(() => {
    !userLoggedIn && resetFirmOffersConfirmation();
  }, [userLoggedIn]);

  useIntersectionObserver(placementRef, handleChasePlacementViewed, observerOptions);

  return <div {...getProps()}>{renderContainerLayout()}</div>;

  function renderContainerLayout() {
    let layout = "";

    if (isLargeOrXlarge) {
      layout = renderLargeContainer();
    } else if (screenSize === MEDIUM) {
      layout = renderMediumContainer();
    } else {
      layout = renderSmallContainer();
    }

    return layout;
  }

  function renderLargeContainer() {
    return (
      <div className={styles.mainLayout}>
        <div className={getClass()}>
          {graphic && renderGraphic(LEFT, MIDDLE)}
          {renderTextContent()}
          {callToAction && renderCallToAction(LEFT, BOTTOM)}
        </div>
        <div className={styles.rightContainer}>
          {graphic && renderGraphic(RIGHT, MIDDLE)}
          {callToAction && renderCallToAction(RIGHT, MIDDLE)}
        </div>
      </div>
    );
  }

  function renderMediumContainer() {
    return (
      <div className={getMediumLayoutClass()}>
        <div className={styles.leftContainer}>
          {graphic && renderGraphic(LEFT, TOP)}
          {renderTextContent()}
          {callToAction && renderCallToAction(LEFT, BOTTOM)}
        </div>
        <div>{graphic && renderGraphic(RIGHT, null)}</div>
      </div>
    );
  }

  function renderSmallContainer() {
    return (
      <div className={styles.mainLayout}>
        {graphic && renderGraphic(LEFT, TOP)}
        {renderTextContent()}
        {graphic && renderGraphic(LEFT, BOTTOM)}
        {disclaimer && renderText(disclaimer)}
        {callToAction && renderCallToAction(LEFT, BOTTOM)}
      </div>
    );
  }

  function renderTextContent() {
    return (
      <div className={styles.textContent}>
        {showTheMath && <ShowTheMath {...getShowTheMathProps()} />}
        {renderUrgencyTriggerBadge() || renderEyebrow()}
        {callout && renderText(callout)}
        {primaryText && renderText(primaryText)}
        {offerDetails && (
          <div className={styles.offerDetails}>{renderOfferDetails(offerDetails, styles)}</div>
        )}
        {secondaryText && renderText(secondaryText)}
        {disclaimer && screenSize !== SMALL && renderText(disclaimer)}
      </div>
    );
  }

  function renderText(textTypeObject) {
    const value = replacePlaceholders(textTypeObject?.value, generateTemplateData());

    return value && <TextContent {...textTypeObject} value={value} />;
  }

  function renderCallToAction(horizontalAlignment, verticalAlignment) {
    return (
      horizontalAlignCTA === horizontalAlignment &&
      verticalAlignCTA === verticalAlignment && (
        <>
          <CallToAction {...getCallToActionProps()} />
        </>
      )
    );
  }

  function renderGraphic(horizontalAlign, verticalAlign) {
    const { height, width } = graphic;
    const { altText, imagePath } = applyOverrides(graphic, screenSize);
    const isHorizontalAlignMatch = horizontalAlignGraphic === horizontalAlign;
    const isVerticalAlignMatch =
      verticalAlign !== null ? verticalAlignGraphic === verticalAlign : true;
    const imageProps = {
      alt: altText,
      className: styles.image,
      fitToContainer: true,
      height,
      src: imagePath,
      width,
    };
    const shouldRenderGraphic = isHorizontalAlignMatch && isVerticalAlignMatch && imagePath;

    return shouldRenderGraphic && <Image {...imageProps} />;
  }

  function renderEyebrow() {
    return eyebrow && <Badge {...getBadgeProps(eyebrow)} />;
  }

  function renderUrgencyTriggerBadge() {
    return (
      command === "URGENCY_TRIGGER" && (
        <div className={classNames(styles.urgencyTriggerBadgeContent)}>
          <UrgencyTriggerBadge commandProps={commandProps} />
        </div>
      )
    );
  }

  function isPlacementViewed() {
    return !!offerIdentifier && viewedOffers[accountNumber]?.includes(offerIdentifier);
  }

  function handleChasePlacementViewed() {
    if (
      isChasePlacement &&
      isChasePrequal &&
      !firmOfferConfirmationCalled &&
      !isPlacementViewed()
    ) {
      triggerFirmOfferConfirmationCall();

      setFirmOfferConfirmationCalled(true);
    }
  }

  async function triggerFirmOfferConfirmationCall() {
    await put(FIRM_OFFER_CONFIRMATION_ENDPOINT, {
      offerIdentifier: offerIdentifier,
    });

    if (isResponseOk(response)) {
      updateViewedOffers();
    } else {
      setFirmOfferConfirmationCalled(false);
    }
  }

  function updateViewedOffers() {
    if (accountNumber) {
      const userViewedOffers = viewedOffers[accountNumber] ?? [];

      userViewedOffers.push(offerIdentifier);
      setViewedOffers({ ...viewedOffers, [accountNumber]: userViewedOffers });
    }
  }

  function getProps() {
    return {
      className: styles.container,
      ref: placementRef,
      style: {
        ...getBackgroundStyle(background, screenSize),
        marginTop: `${offset}rem`,
      },
    };
  }

  function getBadgeProps(eyebrowBadge) {
    const { badgeStyle, badgeType, foregroundColor, prefixIcon, suffixIcon, value } = eyebrowBadge;

    return {
      children: getEyebrowText(prefixIcon, suffixIcon, value),
      className: styles.eyebrowBadge,
      foregroundColor,
      padding: "xsmall",
      styleType: badgeStyle,
      type: badgeType,
    };
  }

  function getIconProps(icon) {
    const { color, name } = icon;

    return {
      className: styles.eyebrowIcon,
      color,
      name,
    };
  }

  function getCallToActionProps() {
    return {
      ...(screenSize === SMALL && { additionalProps: { fullWidth: true } }),
      ...callToAction,
      ...placementData,
      className: styles.ctaButton,
    };
  }

  function getShowTheMathProps() {
    return {
      ...placementData,
      redeemablePoints,
      showTheMath,
      userLoggedIn,
    };
  }

  function getEyebrowText(prefixIcon, suffixIcon, value) {
    return (
      <div className={styles.eyebrowText}>
        {prefixIcon && <Icon {...getIconProps(prefixIcon)} />}
        <TextContent value={value} />
        {suffixIcon && <Icon {...getIconProps(suffixIcon)} />}
      </div>
    );
  }

  function getBackgroundStyle(backgroundObject, screen) {
    const { color, direction, imagePath, theme, type } = applyOverrides(backgroundObject, screen);

    return {
      ...(type === "image" && { backgroundImage: `url(${imagePath})` }),
      ...(type === "color" && { backgroundColor: color }),
      ...(type === "gradient" && {
        backgroundImage: getLinearGradient(theme, direction),
      }),
    };
  }

  function getClass() {
    return classNames({
      [styles.leftContainer]: horizontalAlignGraphic !== "left",
      [styles.leftContainerWithGraphic]: horizontalAlignGraphic === "left",
    });
  }

  function getMediumLayoutClass() {
    return classNames({
      [styles.mainLayout]: true,
      [styles.mediumLayoutCenterAlign]: verticalAlignGraphic === "middle",
      [styles.mediumLayout]: verticalAlignGraphic === "top",
    });
  }

  function generateTemplateData() {
    return {
      ...[userLoggedIn && firstName],
      ...[userLoggedIn && lastName],
      ...additionalProps?.templateData,
    };
  }

  function replacePlaceholders(template, templateData) {
    return template.replace(/\$\{(.*?)\}/g, (match, key) =>
      key in templateData ? templateData[key]?.toLocaleString() : match
    );
  }

  function resetFirmOffersConfirmation() {
    setViewedOffers([]);
    setFirmOfferConfirmationCalled(false);
  }
};

OffersPlacement.displayName = "OffersPlacement";
OffersPlacement.propTypes = {
  /** Object that contains placement props including background, graphic, text, etc */
  placement: offersPlacementPropType,
};
